// src/components/modals/BeginnerTourStartModal.tsx
import appSettings from "@/appsettings.json";
import {
    Box,
    Button,
    Group,
    Skeleton,
    Stack,
    Text
} from "@mantine/core";
import React, { useEffect, useState } from "react";
import ConductionModal from "../ConductionModal";

type BeginnerTourStartModalProps = {
    onClose: () => void;
    onCreateAccount: () => void;
    onStartTour: () => void;
    isGuestAccount: boolean;
};

// define CSS animation globally via <style> tag
const injectPulseAnimation = () => {
    if (document.getElementById("pulse-keyframes")) return;
    const style = document.createElement("style");
    style.id = "pulse-keyframes";
    style.innerHTML = `
    @keyframes pulseButton {
      0%, 100% { transform: scale(1); }
      50% { transform: scale(1.08); }
    }
  `;
    document.head.appendChild(style);
};

const BeginnerTourStartModal: React.FC<BeginnerTourStartModalProps> = ({
    onClose,
    onCreateAccount,
    onStartTour,
    isGuestAccount,
}) => {
    const videoUrl = `${appSettings.S3BucketBaseUrl}Videos/BeginnerTour.mp4`;

    const [videoLoaded, setVideoLoaded] = useState(false);
    useEffect(() => {
        setVideoLoaded(false);
    }, []);

    useEffect(() => {
        injectPulseAnimation();
    }, []);

    return (
        <ConductionModal
            onClose={onClose}
            centered
            withCloseButton={false}
            closeOnClickOutside={false}
            size="98%"
            radius="md"
            padding="lg"
            title="Welcome to Conduction, where your music creations come to life!"
            styles={{
                content: {
                    display: "flex",
                    flexDirection: "column",
                    maxWidth: "1280px",
                    marginInline: "auto",
                },
            }}
        >
            <Stack gap="lg" style={{ minHeight: "60vh" }}>
                {/* Large centered video */}

                <Text>
                    Here's how it works:
                </Text>

                <Box
                    w="100%"
                    style={{
                        display: "grid",
                        placeItems: "center",
                        flex: 1,
                        minHeight: "40vh",
                    }}
                >
                    {!videoLoaded && (
                        <Skeleton
                            visible
                            animate
                            radius="md"
                            w="100%"
                            maw="1000px"
                            h="100%"
                            mah="600px"
                        />
                    )}

                    <video
                        src={videoUrl}
                        loop
                        muted
                        playsInline
                        preload="auto"
                        onLoadedData={() => setVideoLoaded(true)}
                        style={
                            videoLoaded
                                ? {
                                    width: "100%",
                                    maxWidth: "1000px",
                                    maxHeight: "600px",
                                    borderRadius: "5px",
                                    objectFit: "contain",
                                    willChange: "transform",
                                    transform: "translateZ(0)",
                                    border: "2px solid black",
                                }
                                : { display: "none" }
                        }
                        controls={true}
                    />
                </Box>

                {/* Bottom actions */}
                <Group justify="space-between" wrap="wrap">
                    {isGuestAccount ? (
                        <>
                            <Button
                                variant="outline"
                                size="md"
                                radius="md"
                                onClick={onStartTour}
                                aria-label="Start with a tour"
                            >
                                Start with a tour!
                            </Button>

                            <Button
                                variant="filled"
                                size="md"
                                radius="md"
                                onClick={onCreateAccount}
                                aria-label="Jump right in and create an account!"
                                style={{
                                    animation: "pulseButton 1.8s ease-in-out infinite",
                                    transformOrigin: "center",
                                }}
                            >
                                Jump right in and create an account!
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button
                                variant="outline"
                                size="md"
                                radius="md"
                                onClick={onClose}
                                aria-label="Jump right in!"
                            >
                                Jump right in!
                            </Button>

                            <Button
                                variant="filled"
                                size="md"
                                radius="md"
                                onClick={onStartTour}
                                aria-label="Start with a tour"
                                style={{
                                    animation: "pulseButton 1.8s ease-in-out infinite",
                                    transformOrigin: "center",
                                }}
                            >
                                Start with a tour!
                            </Button>
                        </>
                    )}
                </Group>
            </Stack>
        </ConductionModal>
    );
};

export default BeginnerTourStartModal;
